import { useCallback, useEffect, useState } from 'react';
import { FORMATO_PREDEFINITO, FORMATI_CARTELLINO } from './CartellinoStampa';

// Impostazioni del cartellino (formato, contenuto, aspetto), salvate nel browser (localStorage):
// alla prossima apertura del modale si ritrovano com'erano. Sono per dispositivo/browser, non per utente.

// Ogni modulo ha le sue impostazioni (chiave diversa): Colata e GRU non si influenzano a vicenda.
const chiaveModulo = (modulo) => `orizondesk.${modulo}.cartellino.v2`;

export const MM_PER_PX = 25.4 / 96; // 1 px CSS = 0,2646 mm

export const COLORI = [
  { id: 'nero', label: 'Nero', hex: '#000000' },
  { id: 'grigio', label: 'Grigio scuro', hex: '#374151' },
  { id: 'blu', label: 'Blu', hex: '#1e3a8a' },
  { id: 'rosso', label: 'Rosso', hex: '#b91c1c' },
];

export const ALLINEAMENTI = [
  { id: 'left', label: 'Sinistra', icona: 'AlignLeft' },
  { id: 'center', label: 'Centro', icona: 'AlignCenter' },
  { id: 'right', label: 'Destra', icona: 'AlignRight' },
];

export const SPAZIATURE = [
  { id: 'compatta', label: 'Compatta', mm: 0.3 },
  { id: 'normale', label: 'Normale', mm: 1 },
  { id: 'ampia', label: 'Ampia', mm: 2 },
];

export const ALTEZZE_BARCODE = [
  { id: 'piccolo', label: 'Piccolo', fattore: 0.27 },
  { id: 'medio', label: 'Medio', fattore: 0.34 },
  { id: 'grande', label: 'Grande', fattore: 0.42 },
];

export const SPESSORI_CORNICE = [
  { id: 'sottile', label: 'Sottile', mm: 0.2 },
  { id: 'normale', label: 'Normale', mm: 0.4 },
  { id: 'spessa', label: 'Spessa', mm: 0.8 },
];

export const FONT_PX = { min: 8, max: 40 };

export const IMPOSTAZIONI_PREDEFINITE = {
  formato: FORMATO_PREDEFINITO.id,
  // contenuto
  mostraNome: true,
  mostraScortaMin: true,
  mostraSap: false,
  mostraUbicazione: false,
  mostraEtichetta: false,
  // aspetto
  fontPx: 20,            // corpo del testo (px); con "adatta" è il massimo
  adatta: true,          // riduce il corpo finché il testo sta nello spazio
  allineamento: 'left',
  spaziatura: 'normale',
  altezzaBarcode: 'medio',
  cornice: true,
  spessoreCornice: 'normale',
  divisori: true,
  maiuscolo: true,
  colore: 'nero',
};

const tra = (v, lista, predefinito) => (lista.some((x) => x.id === v) ? v : predefinito);
const bool = (v, predefinito) => (typeof v === 'boolean' ? v : predefinito);

/** Unisce i valori salvati ai predefiniti scartando tutto ciò che non è valido (dati vecchi o manomessi). */
export const normalizza = (grezzo) => {
  const d = IMPOSTAZIONI_PREDEFINITE;
  const g = grezzo && typeof grezzo === 'object' ? grezzo : {};
  const px = Number(g.fontPx);
  return {
    formato: tra(g.formato, FORMATI_CARTELLINO, d.formato),
    mostraNome: bool(g.mostraNome, d.mostraNome),
    mostraScortaMin: bool(g.mostraScortaMin, d.mostraScortaMin),
    mostraSap: bool(g.mostraSap, d.mostraSap),
    mostraUbicazione: bool(g.mostraUbicazione, d.mostraUbicazione),
    mostraEtichetta: bool(g.mostraEtichetta, d.mostraEtichetta),
    fontPx: Number.isFinite(px) ? Math.min(FONT_PX.max, Math.max(FONT_PX.min, Math.round(px))) : d.fontPx,
    adatta: bool(g.adatta, d.adatta),
    allineamento: tra(g.allineamento, ALLINEAMENTI, d.allineamento),
    spaziatura: tra(g.spaziatura, SPAZIATURE, d.spaziatura),
    altezzaBarcode: tra(g.altezzaBarcode, ALTEZZE_BARCODE, d.altezzaBarcode),
    cornice: bool(g.cornice, d.cornice),
    spessoreCornice: tra(g.spessoreCornice, SPESSORI_CORNICE, d.spessoreCornice),
    divisori: bool(g.divisori, d.divisori),
    maiuscolo: bool(g.maiuscolo, d.maiuscolo),
    colore: tra(g.colore, COLORI, d.colore),
  };
};

const leggi = (modulo) => {
  try {
    return normalizza(JSON.parse(window.localStorage.getItem(chiaveModulo(modulo)) || 'null'));
  } catch (e) {
    return normalizza(null); // localStorage non disponibile o JSON rovinato
  }
};

/** [impostazioni, imposta(campo, valore), ripristina()] con salvataggio automatico nel browser. */
export const useImpostazioniCartellino = (modulo = 'colata') => {
  const [imp, setImp] = useState(() => leggi(modulo));

  useEffect(() => {
    try {
      window.localStorage.setItem(chiaveModulo(modulo), JSON.stringify(imp));
    } catch (e) {
      // spazio esaurito o modalità privata: le impostazioni valgono solo per questa sessione
    }
  }, [imp, modulo]);

  const imposta = useCallback((campo, valore) => setImp((p) => normalizza({ ...p, [campo]: valore })), []);
  const ripristina = useCallback(() => setImp(normalizza(null)), []);
  return [imp, imposta, ripristina];
};
